<template>
  <div>
    <section v-if="tourOwnedParks.length > 0" class="tw:mb-6 tw:pb-6 tw:border-b tw:border-zinc-200">
      <h5 class="tw:text-xs tw:font-semibold tw:uppercase tw:tracking-wide tw:text-zinc-500 tw:mb-3">
        {{ $t('Event park', tourOwnedParks.length) }}
      </h5>
      <Alert v-if="disallowBrandParks" variant="warning" class="tw:mb-3">
        {{ $t('The park for this event is defined by the organizer and applied automatically. There is no park to select, but you still need to add your items to the event park to make them available.', tourOwnedParks.length) }}
      </Alert>
      <div class="list-group list-group-flush">
        <Park v-for="park in tourOwnedParks"
              :key="'tour-' + park.id"
              :disabled="true"
              :park="park"
              is-selected
              :ownerable="tourName"
              class="list-group-item px-2">
          <template #badge>
            <Badge variant="zinc-light">
              <SparklesIcon class="tw:size-3" />
              {{ $t('Applied automatically') }}
            </Badge>
          </template>
        </Park>
      </div>
    </section>

    <section>
      <h5 class="tw:text-xs tw:font-semibold tw:uppercase tw:tracking-wide tw:text-zinc-500 tw:mb-3">
        {{ $t('Owned park', parksAvailable.length) }}
      </h5>
      <FuseSearch
        :data="parksAvailable"
        :keys="['name', 'ownerable.name']"
        :exact-match-number="0.2"
      >
        <template #search="{ modelValue, updateModelValue }">
          <input
            type="search"
            :value="modelValue"
            @input="updateModelValue(($event.target).value)"
            :placeholder="$t('Search')"
            class="form-control tw:mb-2"
          />
        </template>
        <template #default="{ results }">
          <div class="list-group list-group-flush">
            <div class="d-flex justify-content-between list-group-item  px-2">
              <div class="form-check">
                <input type="checkbox"
                       :id="'parkCheckToggle-' + parkable.id + '-' + parkableType"
                       :checked="areAllSelected(results)"
                       class="form-check-input"
                       @change="toggleSelection($event.target.checked, results)"
                >
                <label class="form-check-label"
                       :for="'parkCheckToggle-' + parkable.id + '-' + parkableType">
                  {{ areAllSelected(results) ? $t('Unselect all') : $t('Select all') }}
                </label>
              </div>
            </div>
          </div>
          <div
            class="list-group list-group-flush overflow-auto tw:max-h-[400px]"
            style="scrollbar-width: thin; scrollbar-color: gray transparent;"
          >
            <Park v-for="park in results"
                  :key="park.id"
                  :park="park"
                  :disabled="isParkDisabled(park)"
                  :is-selected="isParkSelected(park)"
                  :ownerable="park.ownerable.name"
                  @updateSelectedPark="updateParkSelection(park, $event)"
                  class="list-group-item px-2">
              <template #badge>
                <Badge v-if="isParkDisabled(park)" variant="zinc-light">
                  <BanIcon class="tw:size-3" />
                  {{ $t('Not available for this event') }}
                </Badge>
              </template>
            </Park>
          </div>
        </template>
      </FuseSearch>
    </section>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, onBeforeUnmount } from "vue";
import Park from "@/components/park/Park.vue";
import FuseSearch from "@/components/atoms/fuse-search/FuseSearch.vue";
import ParkModel from "@/models/Park";
import Alert from "@/components/ui/Alert.vue";
import Badge from "@/components/ui/badge/Badge.vue";
import { SparklesIcon, BanIcon } from "@lucide/vue";

const props = defineProps({
  parkable: {
    type: Object,
    required: true,
  },
  parkableType: {
    type: String,
    required: true,
  },
  parksAvailable: {
    type: Array,
    required: true,
  },
  filters: {
    type: Object,
    default: () => ({}),
  },
  tourOwnedParks: {
    type: Array,
    default: () => [],
  },
  tourName: {
    type: String,
    default: '',
  },
  disallowBrandParks: {
    type: Boolean,
    default: false,
  },
});

const isParkDisabled = (park) => {
  return props.disallowBrandParks && park.ownerable_type === 'App\\Models\\Brand';
};

const emit = defineEmits(['loaded', 'pending-changes', 'selection-changed']);

const parkSelected = ref([]);
const hasChanges = ref(false);

const filterKey = computed(() => {
  if (props.parkableType === 'App\\Models\\Stage') {
    return 'stage_id';
  }
  if (props.parkableType === 'App\\Models\\Store') {
    return 'store_id';
  }
  return 'tour_id';
});

const fetchSelectedParks = async () => {
  const filters = {
    ...props.filters,
    [filterKey.value]: props.parkable.id,
  };

  let parkModel = ParkModel;
  Object.entries(filters).forEach(([key, value]) => {
    parkModel = parkModel.where(key, value);
  });
  const parks = await parkModel.get();

  parkSelected.value = parks.data;
  hasChanges.value = false;

  emit('loaded', parkSelected.value.length);
};

onMounted(() => {
  fetchSelectedParks();
});

onBeforeUnmount(() => {
  if (hasChanges.value) {
    emit('pending-changes', getSelectionPayload());
  }
});

const isParkSelected = (park) => {
  return parkSelected.value.some(p => parseInt(p.id) === parseInt(park.id));
};

const selectableResults = (results) => results.filter(park => !isParkDisabled(park));

const areAllSelected = (results) => {
  const selectable = selectableResults(results);
  if (selectable.length === 0) {
    return false;
  }
  return selectable.every(park => isParkSelected(park));
};

const toggleSelection = (selectAll, results) => {
  hasChanges.value = true;
  const selectable = selectableResults(results);
  if (selectAll) {
    const currentIds = new Set(parkSelected.value.map(p => parseInt(p.id)));
    const newParks = selectable.filter(p => !currentIds.has(parseInt(p.id)));
    parkSelected.value = [...parkSelected.value, ...newParks];
  } else {
    const selectableIds = new Set(selectable.map(p => parseInt(p.id)));
    parkSelected.value = parkSelected.value.filter(p => !selectableIds.has(parseInt(p.id)));
  }
  emit('selection-changed', parkSelected.value.length);
};

const updateParkSelection = (park, isSelected) => {
  hasChanges.value = true;
  if (isSelected) {
    if (!parkSelected.value.some(p => parseInt(p.id) === parseInt(park.id))) {
      parkSelected.value.push(park);
    }
  } else {
    const parkIndex = parkSelected.value.findIndex(p => parseInt(p.id) === parseInt(park.id));
    parkSelected.value.splice(parkIndex, 1);
  }
  emit('selection-changed', parkSelected.value.length);
};

const getSelectionPayload = () => {
  return {
    parkable_type: props.parkableType,
    parkable_id: props.parkable.id,
    parks: props.parksAvailable.map(p => ({
      id: p.id,
      selected: isParkSelected(p),
    })),
  };
};

defineExpose({
  hasChanges,
  getSelectionPayload,
  selectedCount: computed(() => parkSelected.value.length),
});
</script>
